import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Share2 } from "lucide-react";
import SEO from "@/components/common/SEO";
import { api, SITE_URL } from "@/lib/api";
import { toast } from "sonner";

// -----------------------------------------------------------------------------
// Markdown-ish renderer — subset used in seeded blog content.
// Supports: h2/h3, blockquote, unordered lists, ordered lists, tables (| a | b |),
// inline bold (**), inline links [text](url), hr (--- or ***).
// -----------------------------------------------------------------------------
function isTableBlock(block) {
    const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
    if (lines.length < 2) return false;
    if (!lines[0].startsWith("|") || !lines[0].endsWith("|")) return false;
    // Second line must be the separator | --- | --- |
    return /^\|[\s\-|:]+\|$/.test(lines[1]);
}

function renderTable(block, key) {
    const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
    const parseRow = (l) =>
        l
            .slice(1, -1) // strip leading/trailing pipes
            .split("|")
            .map((c) => c.trim());
    const header = parseRow(lines[0]);
    const rows = lines.slice(2).map(parseRow);
    return (
        <div key={key} className="my-8 overflow-x-auto border border-[var(--line)]">
            <table className="w-full border-collapse text-left">
                <thead>
                    <tr className="bg-[#0d0d0d] border-b-2 border-[var(--danger)]">
                        {header.map((h, i) => (
                            <th
                                key={i}
                                className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--warning)] px-4 py-3 align-top"
                            >
                                {renderInline(h)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, ri) => (
                        <tr
                            key={ri}
                            className={
                                ri % 2 === 0
                                    ? "bg-transparent border-t border-dashed border-[var(--ink-dim)]"
                                    : "bg-[#0a0a0a] border-t border-dashed border-[var(--ink-dim)]"
                            }
                        >
                            {row.map((c, ci) => (
                                <td
                                    key={ci}
                                    className={`font-mono text-xs md:text-sm px-4 py-3 align-top leading-relaxed ${
                                        ci === 0
                                            ? "text-[var(--paper-2)] font-semibold"
                                            : "text-[var(--paper)]"
                                    }`}
                                >
                                    {renderInline(c)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function renderContent(md) {
    const blocks = md.split(/\n\n+/);
    return blocks.map((block, i) => {
        const b = block.trim();
        if (!b) return null;

        if (isTableBlock(b)) return renderTable(b, i);

        if (b.startsWith("## ")) {
            return (
                <h2
                    key={i}
                    className="font-display text-3xl md:text-4xl mt-14 mb-4 text-[var(--paper-2)] leading-tight"
                >
                    {renderInline(b.replace(/^##\s+/, ""))}
                </h2>
            );
        }
        if (b.startsWith("### ")) {
            return (
                <h3
                    key={i}
                    className="font-display text-xl md:text-2xl mt-10 mb-3 text-[var(--paper-2)] leading-snug"
                >
                    {renderInline(b.replace(/^###\s+/, ""))}
                </h3>
            );
        }
        if (b.startsWith("> ")) {
            return (
                <blockquote
                    key={i}
                    className="border-l-2 border-[var(--danger)] pl-6 my-8 font-display italic text-xl md:text-2xl text-[var(--paper-2)] leading-tight"
                >
                    {renderInline(b.replace(/^>\s+/gm, ""))}
                </blockquote>
            );
        }
        if (b.startsWith("- ")) {
            const items = b.split("\n").filter((l) => l.trim().startsWith("- "));
            return (
                <ul key={i} className="my-5 space-y-3">
                    {items.map((it, j) => (
                        <li
                            key={j}
                            className="font-mono text-sm md:text-base text-[var(--paper)] flex gap-3 leading-relaxed"
                        >
                            <span className="text-[var(--danger)] font-bold flex-shrink-0">→</span>
                            <span>{renderInline(it.replace(/^-\s+/, ""))}</span>
                        </li>
                    ))}
                </ul>
            );
        }
        if (/^\d+\.\s/.test(b)) {
            const items = b.split("\n").filter((l) => /^\d+\.\s/.test(l.trim()));
            return (
                <ol key={i} className="my-5 space-y-3 list-none counter-reset-item">
                    {items.map((it, j) => (
                        <li
                            key={j}
                            className="font-mono text-sm md:text-base text-[var(--paper)] flex gap-3 leading-relaxed"
                        >
                            <span className="text-[var(--warning)] font-bold flex-shrink-0">
                                {String(j + 1).padStart(2, "0")}.
                            </span>
                            <span>{renderInline(it.replace(/^\d+\.\s+/, ""))}</span>
                        </li>
                    ))}
                </ol>
            );
        }
        if (b === "---" || b === "***" || b === "—") {
            return (
                <hr
                    key={i}
                    className="my-10 border-t border-dashed border-[var(--ink-dim)]"
                />
            );
        }
        // Paragraph (may contain inline ** bold, links)
        return (
            <p
                key={i}
                className="font-mono text-base md:text-lg leading-[1.8] text-[var(--paper)] my-5"
            >
                {renderInline(b)}
            </p>
        );
    });
}

function renderInline(text) {
    // Split by bold or link tokens
    const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
    return parts.map((part, idx) => {
        if (!part) return null;
        if (part.startsWith("**") && part.endsWith("**")) {
            return (
                <strong key={idx} className="text-[var(--paper-2)] font-bold">
                    {part.slice(2, -2)}
                </strong>
            );
        }
        const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (linkMatch) {
            const [, label, href] = linkMatch;
            const isExternal = /^https?:/.test(href);
            return (
                <a
                    key={idx}
                    href={href}
                    target={isExternal ? "_blank" : undefined}
                    rel={isExternal ? "noopener noreferrer" : undefined}
                    className="text-[var(--warning)] underline hover:text-[var(--danger)]"
                >
                    {label}
                </a>
            );
        }
        return <React.Fragment key={idx}>{part}</React.Fragment>;
    });
}

// Extract FAQ items from markdown content — every `### Question?` under a
// `## Preguntas frecuentes` (or "Preguntas frecuentes / FAQ") heading is used.
function extractFaqs(md) {
    if (!md) return [];
    const lines = md.split("\n");
    let inFaqSection = false;
    const items = [];
    let currentQ = null;
    let currentA = [];

    const push = () => {
        if (currentQ && currentA.length) {
            items.push({ q: currentQ, a: currentA.join(" ").trim() });
        }
        currentQ = null;
        currentA = [];
    };

    for (const raw of lines) {
        const l = raw.trim();
        if (l.startsWith("## ")) {
            push();
            const t = l.replace(/^##\s+/, "").toLowerCase();
            inFaqSection = t.includes("preguntas frecuentes") || t.startsWith("faq");
            continue;
        }
        if (!inFaqSection) continue;
        if (l.startsWith("### ")) {
            push();
            currentQ = l.replace(/^###\s+/, "");
            continue;
        }
        if (currentQ && l && !l.startsWith("---")) {
            currentA.push(l.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1").replace(/\*\*/g, ""));
        }
    }
    push();
    return items;
}

export default function BlogPost() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setPost(null);
        setNotFound(false);
        api.get(`/blog/${slug}`)
            .then(({ data }) => setPost(data))
            .catch(() => setNotFound(true));
    }, [slug]);

    function share() {
        const url = `${SITE_URL}/blog/${slug}`;
        if (navigator.share) {
            navigator.share({ title: post?.title, url }).catch(() => {});
        } else {
            navigator.clipboard.writeText(url);
            toast.success("Enlace copiado al portapapeles");
        }
    }

    const faqs = useMemo(() => (post ? extractFaqs(post.content) : []), [post]);

    if (notFound) {
        return (
            <section className="py-32">
                <div className="container-x text-center max-w-xl mx-auto">
                    <p className="eyebrow justify-center mb-6">
                        Error 404 · Entrada perdida
                    </p>
                    <h1 className="font-display text-5xl md:text-7xl mb-6">
                        Esta cicatriz aún no existe.
                    </h1>
                    <Link
                        to="/blog"
                        className="btn-brutal"
                        data-testid="back-to-blog"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Volver al archivo
                    </Link>
                </div>
            </section>
        );
    }

    if (!post) {
        return (
            <section className="py-32 text-center">
                <p className="font-mono text-sm uppercase tracking-[0.3em] text-[var(--ink-muted)]">
                    Cargando expediente…
                </p>
            </section>
        );
    }

    const canonical = `${SITE_URL}/blog/${post.slug}`;
    const cover = post.cover_image
        ? post.cover_image.startsWith("http")
            ? post.cover_image
            : `${SITE_URL}${post.cover_image}`
        : "https://customer-assets.emergentagent.com/job_autodestruccion-sa/artifacts/96mtdjwo_image.png";

    const articleJson = {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.title,
        description: post.excerpt,
        image: [cover],
        author: {
            "@type": "Person",
            name: "Nelson González Rodríguez",
            alternateName: "NiTanAdicto",
            url: `${SITE_URL}/nelson-gonzalez-rodriguez`,
            sameAs: [
                "https://www.linkedin.com/in/nelsongonzalezr/",
                "https://sinadicciones.org",
                "https://plansobrio.com",
            ],
        },
        publisher: {
            "@type": "Organization",
            name: "El Mapa Inverso",
            logo: {
                "@type": "ImageObject",
                url: "https://customer-assets.emergentagent.com/job_autodestruccion-sa/artifacts/96mtdjwo_image.png",
            },
        },
        datePublished: post.created_at,
        dateModified: post.updated_at,
        url: canonical,
        mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
        articleSection: post.category,
        inLanguage: "es",
        isAccessibleForFree: true,
    };

    const breadcrumbJson = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Inicio", item: SITE_URL + "/" },
            { "@type": "ListItem", position: 2, name: "Archivo", item: SITE_URL + "/blog" },
            { "@type": "ListItem", position: 3, name: post.title, item: canonical },
        ],
    };

    const jsonLd = [articleJson, breadcrumbJson];
    if (faqs.length > 0) {
        jsonLd.push({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
        });
    }

    return (
        <>
            <SEO
                title={post.title}
                description={post.excerpt}
                path={`/blog/${post.slug}`}
                image={cover}
                imageAlt={`${post.title} — Nelson González Rodríguez`}
                type="article"
                article={{
                    publishedTime: post.created_at,
                    modifiedTime: post.updated_at,
                    author: "Nelson González Rodríguez",
                    section: post.category,
                }}
                jsonLd={jsonLd}
            />

            <article
                className="py-16 md:py-24"
                data-testid="blog-post"
                itemScope
                itemType="https://schema.org/Article"
            >
                <div className="container-x max-w-3xl">
                    <Link
                        to="/blog"
                        className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--ink-muted)] hover:text-[var(--danger)] mb-10"
                        data-testid="back-link"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        Archivo
                    </Link>

                    <header className="mb-8 pb-8 border-b border-[var(--line)]">
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--danger)] mb-4">
                            <span itemProp="articleSection">{post.category}</span> ·{" "}
                            <time itemProp="datePublished" dateTime={post.created_at}>
                                {new Date(post.created_at).toLocaleDateString("es-CL", {
                                    year: "numeric",
                                    month: "long",
                                    day: "2-digit",
                                })}
                            </time>
                        </p>
                        <h1
                            className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight mb-6 text-[var(--paper-2)]"
                            itemProp="headline"
                        >
                            {post.title}
                        </h1>
                        <p
                            className="font-mono text-lg text-[var(--paper)] leading-relaxed"
                            itemProp="description"
                        >
                            {post.excerpt}
                        </p>
                        <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--ink-muted)]">
                            Por{" "}
                            <Link
                                to="/nelson-gonzalez-rodriguez"
                                className="text-[var(--paper-2)] hover:text-[var(--warning)] underline"
                                itemProp="author"
                            >
                                Nelson González Rodríguez
                            </Link>{" "}
                            · @NiTanAdicto
                        </p>
                    </header>

                    {post.cover_image && (
                        <figure
                            className="mb-12 -mx-4 md:mx-0"
                            data-testid="blog-post-cover"
                        >
                            <img
                                src={cover}
                                alt={`${post.title} — imagen editorial de portada.`}
                                className="w-full aspect-[16/9] object-cover border-2 border-[var(--line)]"
                                loading="eager"
                                itemProp="image"
                            />
                        </figure>
                    )}

                    <div className="prose-dark" data-testid="blog-post-content" itemProp="articleBody">
                        {renderContent(post.content)}
                    </div>

                    <footer className="mt-16 pt-10 border-t border-[var(--line)]">
                        <div className="flex flex-wrap justify-between gap-4 mb-10">
                            <button
                                onClick={share}
                                className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--paper-2)] hover:text-[var(--danger)]"
                                data-testid="share-button"
                            >
                                <Share2 className="w-3.5 h-3.5" />
                                Compartir
                            </button>
                            <Link
                                to="/salida"
                                className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--warning)] hover:text-[var(--danger)]"
                                data-testid="post-salida-link"
                            >
                                ¿Te reflejó? Salida de emergencia →
                            </Link>
                        </div>

                        {/* Author bio card — E-E-A-T signal for Google */}
                        <div
                            className="border-2 border-[var(--line)] bg-[#0d0d0d] p-6 md:p-8 flex flex-col sm:flex-row gap-6"
                            data-testid="author-bio-card"
                            itemScope
                            itemType="https://schema.org/Person"
                        >
                            <img
                                src="https://customer-assets.emergentagent.com/job_autodestruccion-sa/artifacts/e20qonn4_image.png"
                                alt="Retrato de Nelson González Rodríguez, autor chileno y fundador de SinAdicciones.org."
                                className="w-24 h-24 sm:w-28 sm:h-28 object-cover border border-[var(--paper-2)] flex-shrink-0"
                                itemProp="image"
                                loading="lazy"
                            />
                            <div>
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--danger)] mb-2">
                                    Escrito por
                                </p>
                                <h3
                                    className="font-display text-2xl md:text-3xl text-[var(--paper-2)] mb-2 leading-tight"
                                    itemProp="name"
                                >
                                    Nelson González Rodríguez
                                </h3>
                                <p
                                    className="font-mono text-xs text-[var(--paper)] leading-relaxed mb-3"
                                    itemProp="description"
                                >
                                    Autor chileno de <em>El Principio del Mapa Inverso</em> y de la
                                    serie <em>Cuentos para adictos</em>. Fundador de{" "}
                                    <a
                                        href="https://sinadicciones.org"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline text-[var(--paper-2)]"
                                    >
                                        SinAdicciones.org
                                    </a>{" "}
                                    y creador de{" "}
                                    <a
                                        href="https://plansobrio.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline text-[var(--paper-2)]"
                                    >
                                        PlanSobrio.com
                                    </a>
                                    .
                                </p>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[10px] uppercase tracking-[0.2em]">
                                    <Link
                                        to="/nelson-gonzalez-rodriguez"
                                        className="text-[var(--warning)] hover:text-[var(--paper-2)]"
                                    >
                                        Bio completa →
                                    </Link>
                                    <a
                                        href="https://www.linkedin.com/in/nelsongonzalezr/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[var(--warning)] hover:text-[var(--paper-2)]"
                                    >
                                        LinkedIn →
                                    </a>
                                </div>
                            </div>
                        </div>
                    </footer>
                </div>
            </article>
        </>
    );
}
